// Whether this deployment can send a text, and what a control that would ask
// for one does when it cannot (#782).
//
// A deployment carries texts only when its SMS transport is Twilio (#440):
// with MESSAGE_SMS_TRANSPORT unset it follows MESSAGE_TRANSPORT, and the
// SendGrid transport refuses every SMS at the seam
// (functions/delivery/transport/index.js). While texts are not carried, every
// control that asks *our server* to text somebody — the cancellation notices'
// SMS channel, a category's SMS Reminders, a student's or a contact's "Send SMS
// lesson reminders", Accounts & Invoices' "Send SMS invoice notifications" —
// stays visible and disabled, with one line saying texts are not available
// (`SMS_UNAVAILABLE_TEXT` in ./notifications.js).
//
// **The web app only.** The phone apps' Text button hands off to the phone's
// own Messages app and is not gated; #782 lists the phones' controls that do
// ask the server for a text.
//
// ── One answer, published by the server ─────────────────────────────────────
//
// The server writes `deployment/messaging` from the same config the transport
// reads (`smsCarried` in functions/delivery/smsGate.js), so the document cannot
// say texts work while the seam refuses them, and a change needs no hosting
// deploy. The web reads that one document through a listener, not a callable
// per render; the server republishes every five minutes, and every open tab
// follows.
//
// ── Unknown is off ──────────────────────────────────────────────────────────
//
// No document, a field that is not exactly `true`, a read refused or failed,
// and a reading served from the local cache rather than the server: all off.
// A teacher who cannot tick a box loses nothing; one who ticks a box that
// cannot work is told later, on the message, that it was refused. The
// listeners ask for metadata changes, so the cache's reading turning into the
// server's is a reading too.
//
// ── A saved setting is not rewritten ────────────────────────────────────────
//
// A studio that already ticked one of these keeps what it saved. While gated
// the control draws it as off and cannot change it (`smsShown`), the form saves
// the value it read, and the server still queues a reminder for such a setting
// and refuses it at the seam, recorded on the message. Once texts are carried,
// those settings apply as saved.

/** Where the server publishes it: `deployment/messaging`, outside any studio. */
export const SMS_GATE_DOC = Object.freeze({ collection: 'deployment', id: 'messaging' });

/** The document the server writes for an answer. */
export function smsGateDocument(available) {
  return { smsAvailable: available === true };
}

/**
 * Whether a reading of the document says texts can be sent.
 * @param {{ exists?: boolean, data?: object|null, fromCache?: boolean }|null} reading
 */
export function smsAvailableFrom(reading) {
  if (!reading || reading.fromCache === true || reading.exists === false) return false;
  return reading.data?.smsAvailable === true;
}

/** What a gated control draws for a stored value: the value, or off. */
export function smsShown(stored, available) {
  return available === true && Boolean(stored);
}

/**
 * The cancellation notices' choice as it may be sent: the SMS half dropped
 * while texts are unavailable, whatever the form's state still holds.
 */
export function smsGatedChoice(chosen, available) {
  if (available === true || !chosen) return chosen;
  return { ...chosen, sms: null };
}
